import { SubscriptionWizardDialog } from './SubscriptionWizardDialog';
import { DowngradeDialog } from './DowngradeDialog';
import { DowngradeComparisonDialog } from './DowngradeComparisonDialog';
import { DowngradeSurveyDialog } from './DowngradeSurveyDialog';
import { UpgradeRequiredDialog } from './UpgradeRequiredDialog';
import { AddonRequiredDialog } from './AddonRequiredDialog';
import { AddonUpdateDialog } from './AddonUpdateDialog';
import { UnpaidInvoicesDialog } from './UnpaidInvoicesDialog';
import { EnableTeamBillingDialog } from './EnableTeamBillingDialog';
import { BillingInformationDialog } from './BillingInformationDialog';
import { ScreenManagementDialog } from './ScreenManagementDialog';
import { MemberDowngradeWarningDialog } from './MemberDowngradeWarningDialog';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import {
  takePostSubscribeThankYou,
  takePostSubscribeToast,
} from '../experiment/wizard/post-subscribe-handoff';
import { SubscriptionWizardDialog as ExperimentSubscriptionWizardDialog } from '../experiment/SubscriptionWizardDialog';
import { isBillingExperimentOn } from '../experiment/is-billing-experiment-on';

/**
 * Mounts all billing dialogs at app level.
 * They open/close via billing-dialog-store (activeDialog enum).
 * Mounted in AppShell when VITE_USE_NATIVE_BILLING=true.
 *
 * The wizard has two arms (see experiment/README.md). Both read the same
 * `activeDialog` store value, so every existing caller — PlanGate, plan-floor
 * errors, SubscriptionPage, the paywall — reaches whichever arm is mounted with
 * no change at the call site. Exactly one is mounted: two would stack focus traps.
 */
/**
 * Re-opens the Thank-You after the post-subscribe page load.
 *
 * The wizard now does its full document swap DURING the transaction rather than when the user
 * closes the confirmation (see post-subscribe-handoff for why), so the receipt arrives here, on
 * the fresh page, and has to be put back on screen. This host is the right place for it: it is
 * mounted app-wide, so it does not matter which landing route the subscribe dropped the user on.
 *
 * Reuses the EXISTING `redirectChargeResult` context that iDEAL/Bancontact returns already use —
 * the wizard hook sees it and jumps straight to FINISH, so no new step-routing was needed. That
 * same flag is also what tells the close handler not to reload a second time.
 *
 * Runs once per mount, and the read is destructive, so a remount cannot re-show a receipt.
 */
function usePostSubscribeThankYou() {
  const openDialog = useBillingDialogStore((s) => s.openDialog);
  const handledRef = useRef(false);
  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    const chargeResult = takePostSubscribeThankYou();
    if (chargeResult) openDialog('subscriptionWizard', { redirectChargeResult: chargeResult });
  }, [openDialog]);
}

/**
 * #2046 — re-raises the confirmation line that a post-downgrade document load destroyed.
 *
 * The downgrade-to-Free flows have to replace the document (that is what lifts the paywall), and
 * a toast raised immediately before that navigation is torn down with the page — so a successful
 * downgrade confirmed itself to nobody. The message is stashed at the call site and re-toasted
 * here, on the fresh document, for the same reason the receipt is: this host is mounted app-wide,
 * so it does not matter which landing route the flow chose.
 *
 * Read is destructive and time-boxed (see post-subscribe-handoff), so a remount cannot repeat it.
 */
function usePostSubscribeToast() {
  const handledRef = useRef(false);
  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    const message = takePostSubscribeToast();
    if (message) toast(message);
  }, []);
}

type BillingDialogsProps = {
  /**
   * True while the `sub-update-v1` arm is still resolving. Passed DOWN from AppShell, which owns
   * the single resolve — not read from a second `useBillingExperimentArm()` call here, which would
   * fire the decider mutation a second time for one company-level fact.
   * Optional so the dev catalog pages, which mount this outside AppShell, keep working: with no
   * resolve above them there is nothing to wait for and `isBillingExperimentOn()` answers from the
   * URL/localStorage override or the build default.
   */
  armLoading?: boolean;
};

export function BillingDialogs({ armLoading = false }: BillingDialogsProps) {
  // Experiment arm only: the production wizard still reloads on close, so it never stashes a
  // receipt and this find is always empty there.
  usePostSubscribeThankYou();
  usePostSubscribeToast();
  return (
    <>
      {/* Which wizard, held until the arm settles. Rendering neither for a beat is invisible: both
          mount closed and open from a click, and by the time anyone clicks, AppShell's resolve has
          long since landed. Mounting the WRONG one is not invisible — it would open, then unmount
          mid-interaction and lose whatever the user had picked. */}
      {armLoading ? null : isBillingExperimentOn() ? (
        <ExperimentSubscriptionWizardDialog />
      ) : (
        <SubscriptionWizardDialog />
      )}
      <MemberDowngradeWarningDialog />
      <DowngradeSurveyDialog />
      <DowngradeComparisonDialog />
      <DowngradeDialog />
      <UpgradeRequiredDialog />
      <AddonRequiredDialog />
      <AddonUpdateDialog />
      <UnpaidInvoicesDialog />
      <EnableTeamBillingDialog />
      <BillingInformationDialog />
      <ScreenManagementDialogFromStore />
    </>
  );
}

/**
 * SP53 — store-driven OVERLAY mount of ScreenManagementDialog.
 * Opened via openScreenManagement({ subAccountId, teamId, totalPairedScreens, messageKey })
 * by callers outside the wizard (e.g. handleSelectFree on LIMIT_QUANTITY error,
 * DowngradeDialog on paid → free downgrade limit). It is NOT part of the activeDialog enum
 * so it stacks on top of the subscription wizard without closing it (scio1 parity).
 * The wizard's existing local mount continues to handle the in-wizard decrease-screen-count
 * flow independently.
 *
 * Exported so `InactiveAccountDialog` (paywall lockout) can mount it directly without
 * pulling in the rest of `<BillingDialogs />`. Trial-expired users hitting the Free-plan
 * downgrade 5555 path need this component in the rendered tree to actually render the
 * dialog when the global store opens it.
 */
export function ScreenManagementDialogFromStore() {
  const open = useBillingDialogStore((s) => s.screenManagementOpen);
  const ctx = useBillingDialogStore((s) => s.screenManagementContext);
  const close = useBillingDialogStore((s) => s.closeScreenManagement);
  return (
    <ScreenManagementDialog
      open={open}
      onClose={close}
      totalPairedScreens={(ctx.totalPairedScreens as number) ?? 0}
      subAccountId={ctx.subAccountId as string | undefined}
      teamId={ctx.teamId as string | undefined}
      messageKey={ctx.messageKey as string | undefined}
      // Parents (DowngradeDialog / DowngradeComparisonDialog) stash a retry
      // callback into the dialog context when they open this dialog after
      // a LIMIT_QUANTITY error so the user can complete the original action
      // (e.g. downgrade to Free) directly from this dialog's Continue button.
      onContinue={ctx.onContinue as (() => void) | undefined}
    />
  );
}
